import { getConfig } from "@stryker/core/api/jobs";
import { QueryKeys } from "@stryker/core/queryKeys";
import { useQuery } from "@tanstack/react-query";
import { type FieldArrayWithId, type UseFieldArrayRemove, useFormContext } from "react-hook-form";
import DeleteIcon from "@/assets/icons/trash.svg?react";
import { Combobox } from "@/components/ui/combobox";
import { FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Spinner } from "@/components/ui/spinner";
import { TextInput } from "@/components/ui/textInput";
import { useQuoteItemOptions } from "@/hooks/useQuoteItemOptions";
import type { QuoteFormSchema } from "@/lib/schemas/createUpdateQuoteSchema";
import { cn } from "@/lib/utils";
import { getMargin } from "@/utils/calculations";

type DesktopQuoteDetailsViewProps = {
  isFetchingInitialQuoteData: boolean;
  tableBorder: string;
  fields: FieldArrayWithId<QuoteFormSchema, "quoteDetails", "id">[];
  remove: UseFieldArrayRemove;
  canViewCostAndMargin: boolean;
  repairSystemId?: string | null;
};

export default function DesktopQuoteDetailsView({
  isFetchingInitialQuoteData,
  tableBorder,
  fields,
  remove,
  canViewCostAndMargin,
  repairSystemId,
}: DesktopQuoteDetailsViewProps) {
  const { register, control, watch, setValue } = useFormContext<QuoteFormSchema>();

  const quoteDetails = watch("quoteDetails");

  const { data: repairUnitTypes } = useQuery({
    queryKey: [QueryKeys.JobsConfig],
    queryFn: getConfig,
    staleTime: 1000000,
    select: (response) => response.repair_unit_types,
  });

  const { itemOptions, getUnitsForItem, isCustomItem } = useQuoteItemOptions(repairSystemId);

  function handleItemChange(index: number, value: string) {
    setValue(`quoteDetails.${index}.item_name`, value);

    if (isCustomItem(value)) {
      setValue(`quoteDetails.${index}.unit`, "");
      return;
    }

    const units = getUnitsForItem(value);
    const defaultUnit = units[0];
    if (defaultUnit) {
      applyUnit(index, defaultUnit);
    }
  }

  function handleUnitChange(index: number, itemName: string, unitValue: string) {
    const selectedUnit = getUnitsForItem(itemName).find((unit) => unit.unit === unitValue);
    if (selectedUnit) {
      applyUnit(index, selectedUnit);
    } else {
      setValue(`quoteDetails.${index}.unit`, unitValue);
    }
  }

  function applyUnit(index: number, unit: { unit: string; cost_per_unit: number; price_per_unit: number }) {
    setValue(`quoteDetails.${index}.unit`, unit.unit);
    setValue(`quoteDetails.${index}.price_per_unit`, unit.price_per_unit);
    if (canViewCostAndMargin) {
      setValue(`quoteDetails.${index}.cost_per_unit`, unit.cost_per_unit);
    }
  }

  return (
    <div className="-mx-4 overflow-x-auto md:mx-0">
      <table className="w-full min-w-[800px] border-collapse border border-gray-300">
        <thead>
          <tr className="bg-gray-100">
            <th className={cn("p-2 text-xs md:text-sm", tableBorder)}>Item</th>
            <th className={cn("p-2 text-xs md:text-sm", tableBorder)}>Quantity</th>
            <th className={cn("p-2 text-xs md:text-sm", tableBorder)}>Unit</th>
            {canViewCostAndMargin && <th className={cn("p-2 text-xs md:text-sm", tableBorder)}>Cost Per Unit</th>}
            <th className={cn("p-2 text-xs md:text-sm", tableBorder)}>Price Per Unit</th>
            {canViewCostAndMargin && <th className={cn("p-2 text-xs md:text-sm", tableBorder)}>Total Cost</th>}
            <th className={cn("p-2 text-xs md:text-sm", tableBorder)}>Total Revenue</th>
            {canViewCostAndMargin && <th className={cn("p-2 text-xs md:text-sm", tableBorder)}>Margin</th>}
            <th className={cn("p-2 text-xs md:text-sm", tableBorder)}>Action</th>
          </tr>
        </thead>
        <tbody>
          {isFetchingInitialQuoteData && (
            <tr>
              <td className={cn("p-2", tableBorder)} colSpan={canViewCostAndMargin ? 9 : 6}>
                <div className="flex justify-center">
                  <Spinner />
                </div>
              </td>
            </tr>
          )}
          {fields.map((field, index) => {
            const currentItemRow = quoteDetails[index];
            const currentItemName = currentItemRow.item_name;
            const currentItemUnit = currentItemRow.unit;
            const currentIsEditable = currentItemRow.is_editable;
            const currentCostPerUnit = currentItemRow.cost_per_unit ?? 0;
            const currentPricePerUnit = currentItemRow.price_per_unit ?? 0;
            const currentQuantity = currentItemRow.quantity ?? 0;
            const itemTotalCosts = currentQuantity * currentCostPerUnit;
            const itemTotalQuotes = currentQuantity * currentPricePerUnit;
            const margin = getMargin({ totalQuotes: itemTotalQuotes, totalCosts: itemTotalCosts });
            const itemIsCustom = isCustomItem(currentItemName);
            const availableUnits = itemIsCustom ? [] : getUnitsForItem(currentItemName);
            const showUnitDropdown = itemIsCustom || availableUnits.length > 1;
            const unitOptions = itemIsCustom
              ? (repairUnitTypes ?? []).map((unit) => ({ label: unit, value: unit }))
              : availableUnits.map((unit) => ({ label: unit.unit, value: unit.unit }));

            return (
              <tr key={field.id}>
                <td className={cn("p-2", tableBorder)}>
                  <div className="flex flex-col gap-2">
                    <FormField
                      control={control}
                      name={`quoteDetails.${index}.item_name`}
                      render={({ field }) => {
                        return (
                          <FormItem>
                            <Combobox
                              disabled={!currentIsEditable}
                              options={itemOptions}
                              value={field.value}
                              onValueChange={(value) => handleItemChange(index, value)}
                              placeholder="Item"
                              searchPlaceholder="Search items..."
                            />
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />
                    {currentItemName === "Custom" && (
                      <TextInput {...register(`quoteDetails.${index}.custom_item_name`)} className="w-full" />
                    )}
                  </div>
                </td>
                <td className={cn("p-2", tableBorder)}>
                  <TextInput
                    type="number"
                    {...register(`quoteDetails.${index}.quantity`, { valueAsNumber: true })}
                    className="w-[83px]"
                  />
                </td>
                <td className={cn("p-2", tableBorder)}>
                  <div className="flex flex-col gap-2">
                    {showUnitDropdown ? (
                      <FormField
                        key={currentItemUnit}
                        control={control}
                        name={`quoteDetails.${index}.unit`}
                        render={({ field }) => {
                          return (
                            <FormItem>
                              <Combobox
                                disabled={!currentIsEditable}
                                options={unitOptions}
                                value={field.value}
                                onValueChange={(value) => handleUnitChange(index, currentItemName, value)}
                                placeholder="Unit"
                                searchPlaceholder="Search units..."
                              />
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />
                    ) : (
                      <TextInput value={currentItemUnit} disabled className="w-full" />
                    )}
                    {currentItemUnit === "CUSTOM" && (
                      <TextInput {...register(`quoteDetails.${index}.custom_unit`)} className="w-full" />
                    )}
                  </div>
                </td>
                {canViewCostAndMargin && (
                  <td className={cn("p-2", tableBorder)}>
                    <TextInput
                      type="number"
                      step={0.01}
                      {...register(`quoteDetails.${index}.cost_per_unit`, { valueAsNumber: true })}
                      className="w-[110px]"
                    />
                  </td>
                )}
                <td className={cn("p-2", tableBorder)}>
                  <TextInput
                    type="number"
                    step={0.01}
                    {...register(`quoteDetails.${index}.price_per_unit`, { valueAsNumber: true })}
                    className="w-[110px]"
                  />
                </td>
                {canViewCostAndMargin && (
                  <td className={cn("p-2 text-center", tableBorder)}>
                    {Number.isNaN(itemTotalCosts) ? "N/A" : `$${itemTotalCosts.toFixed(2)}`}
                  </td>
                )}
                <td className={cn("p-2 text-center", tableBorder)}>
                  {Number.isNaN(itemTotalQuotes) ? "N/A" : `$${itemTotalQuotes.toFixed(2)}`}
                </td>
                {canViewCostAndMargin && (
                  <td
                    className={cn(
                      "p-2 text-center",
                      margin < 0 ? "bg-red-600 text-white" : "bg-green-300",
                      tableBorder
                    )}
                  >
                    {margin.toFixed(2)}%
                  </td>
                )}
                <td className={cn("p-2 text-center", tableBorder)}>
                  <button type="button" onClick={() => remove(index)} className="cursor-pointer text-red-500">
                    <DeleteIcon />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
